import { useIntl } from 'localization';

import { C, ThresholdConfig, formatRatePct, rateColor, rateSeverity } from '../../MapSharedData';
import { PostcodeTooltip } from '../types';

interface Props {
  tooltip: PostcodeTooltip;
  cfg: ThresholdConfig;
}

const MAX_BREAKDOWN_ROWS = 5;

export function PostcodeTooltipCard({ tooltip, cfg }: Props) {
  const { formatMessage } = useIntl();
  const { count, sent, place, region, postcode, incidents } = tooltip.marker;
  const hasData = count > 0;
  const sev = rateSeverity(count, sent, cfg);

  const breakdown = [...(incidents ?? [])].sort((a, b) => b.count - a.count);
  const shownBreakdown = breakdown.slice(0, MAX_BREAKDOWN_ROWS);
  const hiddenBreakdown = breakdown.length - shownBreakdown.length;
  return (
    <div
      className="fixed rounded-10 px-3.5 py-2.5 text-[13px] pointer-events-none z-[9999] shadow-popup min-w-[180px] max-w-[280px]"
      style={{ left: tooltip.x + 14, top: tooltip.y - 10, background: C.textPrimary, color: C.bg }}
    >
      <div className="flex items-center gap-2 mb-1">
        <div
          className="w-2.5 h-2.5 rounded-full border-[1.5px] border-white"
          style={{ background: rateColor(count, sent, cfg) }}
        />
        <span className="font-semibold">{postcode}</span>
      </div>
      {(place || region) && (
        <div className="text-xs text-v2blueGray-200 mb-1.5">
          {[place, region].filter(Boolean).join(' · ')}
        </div>
      )}
      {hasData ? (
        <>
          {sev.insufficient ? (
            <div style={{ color: sev.color }}>
              {formatMessage({ id: sev.labelId })}
              {sent != null && (
                <span className="text-[11px] text-v2blueGray-400">
                  {' · '}
                  {formatMessage(
                    { id: 'analytics.incidentMap.ordersRatio' },
                    { count: count.toLocaleString(), total: sent.toLocaleString() },
                  )}
                </span>
              )}
            </div>
          ) : (
            <>
              {sent != null ? (
                <div style={{ color: sev.color }}>
                  <span className="font-semibold">{formatRatePct(count, sent)}</span>{' '}
                  <span className="text-[11px] text-v2blueGray-400">
                    {formatMessage(
                      { id: 'analytics.incidentMap.ordersRatio' },
                      { count: count.toLocaleString(), total: sent.toLocaleString() },
                    )}
                  </span>
                </div>
              ) : (
                <div style={{ color: sev.color }}>
                  {formatMessage({ id: 'analytics.incidentMap.ordersCount' }, { count })}
                </div>
              )}
              <div className="text-[11px] mt-0.5" style={{ color: sev.color }}>
                {formatMessage({ id: sev.labelId })}
              </div>
            </>
          )}
          {shownBreakdown.length > 0 && (
            <div className="mt-2 pt-1 border-t border-white/15 flex flex-col">
              <div className="text-[10px] uppercase tracking-[0.06em] text-v2blueGray-400 mb-1">
                {formatMessage({ id: 'analytics.incidentMap.ordersByType' })}
              </div>
              {shownBreakdown.map((inc) => (
                <div
                  key={`${inc.code}-${inc.courier}`}
                  className="flex items-start justify-between gap-2 py-1.5 border-b border-white/10 last:border-b-0"
                >
                  <div className="min-w-0">
                    <div className="text-[11px] text-v2blueGray-200 break-words">
                      {formatMessage({
                        id: `notifs.filters.exception-type.${inc.code.toLowerCase()}`,
                        defaultMessage: inc.code,
                      })}
                    </div>
                    <div className="text-[10px] text-v2blueGray-400 mt-0.5">
                      {formatMessage({
                        id: `general.courier.${inc.courier.toLowerCase()}`,
                        defaultMessage: inc.courier,
                      })}
                    </div>
                  </div>
                  <span className="text-[11px] font-semibold shrink-0 tabular-nums">
                    {inc.count.toLocaleString()}
                  </span>
                </div>
              ))}
              {hiddenBreakdown > 0 && (
                <div className="text-[11px] text-v2blueGray-400 pt-1.5">
                  {formatMessage({ id: 'analytics.incidentMap.more' }, { count: hiddenBreakdown })}
                </div>
              )}
            </div>
          )}
          <div className="text-[11px] text-v2blueGray-500 mt-1.5 italic">
            {formatMessage({ id: 'analytics.incidentMap.tooltip.clickFocus' })}
          </div>
        </>
      ) : (
        <div className="text-v2blueGray-300">
          {formatMessage({ id: 'analytics.incidentMap.noIncidents' })}
        </div>
      )}
    </div>
  );
}
